import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import CarCard from '../ui/CarCard';
import { renterService } from '../../services/renter.service';
import { showAppAlert } from '../../utils/appAlert';

const FeaturedFleet = ({ cars: propCars = [], categories = [], isLoading }) => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [wishlistIds, setWishlistIds] = useState([]);
  const filters = useMemo(() => ['All', ...categories], [categories]);
  const isRenter = localStorage.getItem('userRole') === 'RENTER' && !!localStorage.getItem('token');

  useEffect(() => {
    if (!filters.includes(activeFilter)) {
      setActiveFilter('All');
    }
  }, [activeFilter, filters]);

  useEffect(() => {
    const loadWishlist = async () => {
      if (!isRenter) {
        setWishlistIds([]);
        return;
      }

      try {
        const ids = await renterService.getWishlistIds();
        setWishlistIds(ids || []);
      } catch (error) {
        setWishlistIds([]);
      }
    };

    loadWishlist();
  }, [isRenter]);

  // Use cars from props
  const displayCars = propCars;

  const filteredCars = activeFilter === 'All'
    ? displayCars
    : displayCars.filter(car => car.categoryName === activeFilter || car.category === activeFilter);

  const handleToggleWishlist = async (car) => {
    if (!isRenter) {
      return;
    }

    const isSaved = wishlistIds.includes(car.id);
    if (isSaved) {
      await renterService.removeFromWishlist(car.id);
      setWishlistIds((current) => current.filter((id) => id !== car.id));
      showAppAlert({
        type: 'success',
        title: 'Removed from wishlist',
        message: 'The vehicle has been removed from your saved cars.'
      });
      return;
    }

    await renterService.addToWishlist(car.id);
    setWishlistIds((current) => [...current, car.id]);
    showAppAlert({
      type: 'success',
      title: 'Added to wishlist',
      message: 'The vehicle has been added to your saved cars.'
    });
  };

  if (isLoading && propCars.length === 0) {
    return (
      <section className="py-8 sm:py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="animate-pulse text-gray-400">Loading fleet...</div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 sm:py-10 bg-white">
      <div className="max-w-7xl mx-auto px-4">

        <p className="text-xs uppercase tracking-[0.2em] text-brand-orange-500 font-semibold mb-5">The Go Rides collection</p>
        {/* Filters */}
        <div className="mb-4">
          <div className="no-scrollbar overflow-x-auto pb-2">
            <div className="inline-flex min-w-max items-center gap-2 rounded-full border border-gray-100 bg-white p-1 shadow-sm">
            {filters.map(filter => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                  activeFilter === filter
                    ? 'bg-brand-black text-white'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {filter}
              </button>
            ))}
            </div>
          </div>
        </div>

        {/* Section Header */}
        <div className="flex justify-between items-end mb-5">
          <div>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-gray-900 mb-2">A ride for every plan.</h2>
            <p className="text-gray-500">Explore the fleet. Find what fits your journey.</p>
          </div>
          <Link to="/browse" className="hidden md:flex items-center text-sm font-bold text-gray-900 hover:text-gray-600 transition-colors">
            View All Cars <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>

        {/* Cars Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredCars.slice(0, 3).map(car => (
            <CarCard
              key={car.id}
              car={car}
              isWishlisted={wishlistIds.includes(car.id)}
              onToggleWishlist={isRenter ? handleToggleWishlist : undefined}
            />
          ))}
        </div>

        {!filteredCars.length && <p className="rounded-2xl bg-gray-50 p-6 text-gray-500">No featured cars in this category yet. Browse the full fleet for more options.</p>}
        {/* Mobile View All Button */}
        <div className="mt-5 text-center md:hidden">
            <Link to="/browse" className="flex items-center justify-center w-full text-sm font-bold text-gray-900 hover:text-gray-600 transition-colors">
              View All Cars <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedFleet;
